/**
 * Module for lazy-loading footer styles and components when the footer becomes visible
 * in the viewport with a specified offset or is already visible on page load.
 */
export const lazyFooter = (() => {
  // eslint-disable-next-line unicorn/no-null
  let footer: HTMLElement | null = null;

  /**
   * Dynamically imports the footer styles.
   *
   * @returns A promise that resolves when the styles are successfully loaded.
   */
  const loadFooterStyles = async (): Promise<void> => {
    await import('../../styles/01-components/04-footer/layout/site-footer.css');
  };

  /**
   * Dynamically imports the privacy settings popup component.
   *
   * @returns A promise that resolves when the component is successfully loaded.
   */
  const loadPrivacySettingsPopup = async (): Promise<void> => {
    const { privacySettingsPopup } = await import(
      '@custom-writing/shared/components/privacy-settings-popup'
    );

    privacySettingsPopup.init();
  };

  /**
   * Handles the visibility of the footer and triggers the lazy loading
   * of footer styles and components when the footer becomes visible
   * within the viewport.
   *
   * @param controller - An AbortController to manage the scroll event listener.
   *
   * @returns A promise that resolves when the styles are loaded or rejects if an error occurs.
   *
   * @throws If an error occurs during the loading of the styles or components.
   */
  const onFooterVisible = async (
    controller: AbortController,
  ): Promise<void> => {
    try {
      await loadFooterStyles();
      await loadPrivacySettingsPopup();
      // Abort the scroll event listener after the styles and components are loaded
      controller.abort();
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('🚨 Error loading footer styles:', error);

      throw new Error(
        `Failed to load footer styles: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  };

  /**
   * Initializes the scroll event listener to check the visibility of the footer.
   * If the footer becomes visible with a 200px offset from the viewport bottom,
   * it triggers the loading of the footer styles and components.
   */
  const initializeFooterOnScroll = (): void => {
    // Create a new AbortController to manage the scroll event listener
    const controller = new AbortController();
    const { signal } = controller;

    // Attach a scroll event listener that checks the footer's visibility with an offset
    window.addEventListener(
      'scroll',
      () => {
        if (
          footer &&
          footer.getBoundingClientRect().top < window.innerHeight + 200
        ) {
          onFooterVisible(controller);
        }
      },
      { signal },
    );
  };

  /**
   * Checks if the footer is immediately visible when the page loads.
   * If visible, it loads the footer styles and components immediately.
   * If not, it sets up a scroll event listener to load the styles and components when the footer becomes visible.
   *
   * @returns A promise that resolves when the footer styles and components are either loaded immediately
   * or set up to be loaded on scroll.
   */
  const checkInitialFooterVisibility = async (): Promise<void> => {
    footer = document.querySelector<HTMLElement>('#siteFooter');

    if (footer) {
      const footerRect = footer.getBoundingClientRect();

      if (footerRect.top < window.innerHeight + 200) {
        // Footer is already within the offset, load styles immediately
        await loadFooterStyles();
        await loadPrivacySettingsPopup();

        return;
      }
    }

    // Otherwise, initialize scroll event listener
    initializeFooterOnScroll();
  };

  return {
    /**
     * Initializes the module by checking the initial visibility of the footer
     * and setting up the scroll event listener if necessary.
     */
    init: () => checkInitialFooterVisibility(),
  };
})();
